import React, { useEffect, useRef, useState } from "react";
import { exportOrder } from "@/lib/exportOrder";
import { useTableSort } from "@/lib/tableSort";

import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ASSIGNABLE_BRANCHES } from "@/lib/constants";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileSpreadsheet, Upload, Loader2, CheckCircle2, AlertTriangle, HelpCircle, Download, FileText, Undo2, FolderPlus } from "lucide-react";

function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={testid}>
      <div className="h-10 w-10 rounded-lg grid place-items-center shrink-0" style={{ background: `${color}1a`, color }}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xl font-bold tabular-nums" style={{ color }}>{value}</div>
        <div className="text-[11px] text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

export default function FullSync() {
  const msT = useTableSort([]);
  const unT = useTableSort([]);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [importBranch, setImportBranch] = useState("Merkez");
  const [imported, setImported] = useState(null);
  const [unassigned, setUnassigned] = useState(null);
  const [picked, setPicked] = useState([]);
  const [targetCommittee, setTargetCommittee] = useState("");
  const [committees, setCommittees] = useState([]);

  useEffect(() => {
    api.get("/committees").then(({ data }) => setCommittees(data)).catch(() => {});
    loadUnassigned();
  }, []);

  const loadUnassigned = () => {
    api.get("/voters/unassigned").then(({ data }) => setUnassigned(data)).catch(() => {});
  };

  const importMissing = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/voters/full-sync/import-missing", null,
        { params: { token: preview.token, branch: importBranch } });
      setImported(data);
      toast.success(`${data.imported} firma eklendi (komite atanmamış)`);
      loadUnassigned();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "İçe aktarma başarısız");
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/voters/full-sync/undo", null, { params: { token: preview.token } });
      toast.success(`${data.reverted} firma eski duruma döndü, ${data.deleted} kayıt silindi`);
      setApplied(null);
      setImported(null);
      loadUnassigned();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Geri alma başarısız");
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/voters/assign-committee",
        { voter_ids: picked, committee_no: Number(targetCommittee) });
      toast.success(`${data.updated} firma Komite ${targetCommittee}'e atandı`);
      setPicked([]);
      loadUnassigned();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Atama başarısız");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    setPreview(null);
    setApplied(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/voters/full-sync/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`${data.rows} satır okundu`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Dosya okunamadı");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/voters/full-sync/apply", null, { params: { token: preview.token } });
      setApplied(data);
      toast.success(`${data.updated} firma FAAL'e çevrildi`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Uygulama başarısız");
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  const download = async (kind, format) => {
    try {
      const order = await exportOrder(
        (kind === "mismatch" ? msT.applySort(preview.mismatch) : (preview[kind] || [])).map((r) => r.sicil_no));
      const res = await api.get("/voters/full-sync/export", {
        params: { token: preview.token, kind, format, only: 1, order }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    }
  };

  return (
    <div className="space-y-5" data-testid="full-sync-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl grid place-items-center bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
          <FileSpreadsheet className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#0F2C59] dark:text-sky-300">FULL Liste Eşitleme</h1>
          <p className="text-sm text-muted-foreground">Oda listesini yükle; unvanı olan firmaları FAAL'e çevir, komite uyuşmazlıklarını gör</p>
        </div>
      </div>

      <Card className="p-4 space-y-3">
        <p className="text-sm text-muted-foreground">
          Dosyanın <b>1. sayfası</b> okunur: <b>Sicil No · Unvan</b> (varsa <b>Seçmen Grup</b>). Eşleştirme sicil no ile yapılır,
          bulunamazsa unvan ile denenir. Unvanı olan ve askıda görünen firmalar FAAL'e çevrilir; hâlihazırda FAAL olanlara dokunulmaz.
          Seçmen Grup kolonu varsa komite uyuşmazlıkları <b>yalnızca listelenir</b>, otomatik değiştirilmez.
        </p>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
          onChange={(e) => upload(e.target.files?.[0])} data-testid="full-sync-file" />
        <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="full-sync-upload">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
          Excel Yükle ve Önizle
        </Button>
      </Card>

      {preview && (
        <Card className="p-4 space-y-4" data-testid="full-sync-preview">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold">{preview.file_name} · {preview.rows} satır okundu</p>
            <Badge variant="secondary" className="text-[11px]">
              Eşleşen {preview.matched}{preview.matched_by_name ? ` (${preview.matched_by_name} unvanla)` : ""}
            </Badge>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat icon={CheckCircle2} label="FAAL'e çevrilecek" value={preview.to_faal_count} color="#16A34A" testid="fs-stat-faal" />
            <Stat icon={AlertTriangle} label="Komite uyuşmazlığı" value={preview.mismatch_count} color="#D97706" testid="fs-stat-mismatch" />
            <Stat icon={HelpCircle} label="Sistemde bulunamadı" value={preview.missing_count} color="#DC2626" testid="fs-stat-missing" />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => download("mismatch", "xlsx")} disabled={!preview.mismatch_count} data-testid="fs-export-mismatch-xlsx">
              <Download className="h-3.5 w-3.5 mr-1.5" />Uyuşmazlık Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => download("mismatch", "pdf")} disabled={!preview.mismatch_count} data-testid="fs-export-mismatch-pdf">
              <FileText className="h-3.5 w-3.5 mr-1.5" />Uyuşmazlık PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => download("faal", "xlsx")} disabled={!preview.to_faal_count} data-testid="fs-export-faal">
              <Download className="h-3.5 w-3.5 mr-1.5" />FAAL Listesi
            </Button>
            <Button size="sm" variant="outline" onClick={() => download("missing", "xlsx")} disabled={!preview.missing_count} data-testid="fs-export-missing">
              <Download className="h-3.5 w-3.5 mr-1.5" />Bulunamayanlar
            </Button>
          </div>

          {preview.mismatch_count > 0 && (
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="px-3 py-2 text-xs font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300">
                Komite uyuşmazlıkları (ilk {Math.min(preview.mismatch.length, 200)} kayıt)
              </div>
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/60 sticky top-0">
                    <tr>
                      <msT.Th sortKey="ms-sicil" get={(r) => r.sicil_no} text className="px-3 py-2 text-left font-medium">Sicil</msT.Th>
                      <msT.Th sortKey="ms-firma" get={(r) => r.company_name} text className="px-3 py-2 text-left font-medium">Firma</msT.Th>
                      <msT.Th sortKey="ms-komite" get={(r) => r.committee_no} className="px-3 py-2 text-left font-medium">Sistemdeki Komite</msT.Th>
                      <msT.Th sortKey="ms-grup" get={(r) => r.group} text className="px-3 py-2 text-left font-medium">Listedeki Grup</msT.Th>
                    </tr>
                  </thead>
                  <tbody>
                    {msT.applySort(preview.mismatch).map((r) => (
                      <tr key={r.voter_id} className="border-t border-border" data-testid={`fs-mismatch-${r.voter_id}`}>
                        <td className="px-3 py-2 tabular-nums">{r.sicil_no || "—"}</td>
                        <td className="px-3 py-2">{r.company_name}<span className="text-muted-foreground">{r.branch ? ` · ${r.branch}` : ""}</span></td>
                        <td className="px-3 py-2">K{r.system_committee_no} {r.system_committee_name}</td>
                        <td className="px-3 py-2 text-amber-700 dark:text-amber-300">{r.list_group}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {applied ? (
            <div className="flex flex-wrap items-center gap-3" data-testid="fs-applied">
              <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                Uygulandı: {applied.updated} firma FAAL'e çevrildi.
              </p>
              <Button variant="outline" size="sm" onClick={undo} disabled={busy} data-testid="fs-undo">
                <Undo2 className="h-3.5 w-3.5 mr-1.5" />Eşitlemeyi Geri Al
              </Button>
            </div>
          ) : (
            <Button onClick={() => setConfirm(true)} disabled={busy || !preview.to_faal_count} data-testid="full-sync-apply">
              {preview.to_faal_count} Firmayı FAAL'e Çevir
            </Button>
          )}

          {preview.missing_count > 0 && (
            <div className="rounded-xl border border-border p-3 space-y-2" data-testid="fs-import-missing-box">
              <p className="text-xs text-muted-foreground">
                Sistemde bulunamayan <b>{preview.missing_count}</b> firmayı yeni kayıt olarak ekleyebilirsin.
                Durum <b>FAAL</b>, komite <b>atanmamış</b> olarak eklenir; aşağıdan komite atarsın.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={importBranch} onValueChange={setImportBranch}>
                  <SelectTrigger className="w-[200px] h-9" data-testid="fs-import-branch"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Merkez">Merkez</SelectItem>
                    {ASSIGNABLE_BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
                {imported ? (
                  <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400" data-testid="fs-imported">
                    {imported.imported} firma eklendi ({imported.branch})
                  </span>
                ) : (
                  <Button size="sm" onClick={importMissing} disabled={busy} data-testid="fs-import-missing">
                    <FolderPlus className="h-3.5 w-3.5 mr-1.5" />{preview.missing_count} Firmayı İçe Aktar
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>
      )}

      {unassigned?.total > 0 && (
        <Card className="p-4 space-y-3" data-testid="fs-unassigned">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold">Komitesi atanmamış firmalar · {unassigned.total}</p>
            <div className="flex items-center gap-2">
              <Select value={targetCommittee} onValueChange={setTargetCommittee}>
                <SelectTrigger className="w-[280px] h-9" data-testid="fs-assign-committee">
                  <SelectValue placeholder="Komite seç" />
                </SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  {committees.map((c) => (
                    <SelectItem key={c.committee_no} value={String(c.committee_no)}>
                      {c.committee_no}. {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={assign} disabled={busy || !picked.length || !targetCommittee} data-testid="fs-assign-btn">
                {picked.length} Firmayı Ata
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={picked.length === unassigned.rows.length && picked.length > 0}
              onCheckedChange={(v) => setPicked(v ? unassigned.rows.map((r) => r.id) : [])}
              data-testid="fs-pick-all"
            />
            <span className="text-muted-foreground">Tümünü seç</span>
          </div>
          <div className="max-h-[380px] overflow-auto rounded-xl border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/60 sticky top-0">
                <tr>
                  <th className="px-3 py-2 w-8"></th>
                  <unT.Th sortKey="un-sicil" get={(r) => r.sicil_no} text className="px-3 py-2 text-left font-medium">Sicil</unT.Th>
                  <unT.Th sortKey="un-firma" get={(r) => r.company_name} text className="px-3 py-2 text-left font-medium">Firma</unT.Th>
                  <unT.Th sortKey="un-sube" get={(r) => r.branch} text className="px-3 py-2 text-left font-medium">Şube</unT.Th>
                </tr>
              </thead>
              <tbody>
                {unT.applySort(unassigned.rows).map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`fs-unassigned-${r.id}`}>
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={picked.includes(r.id)}
                        onCheckedChange={(v) => setPicked((p) => (v ? [...p, r.id] : p.filter((x) => x !== r.id)))}
                        data-testid={`fs-pick-${r.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 tabular-nums">{r.sicil_no || "—"}</td>
                    <td className="px-3 py-2">{r.company_name}</td>
                    <td className="px-3 py-2">{r.branch}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>FAAL güncellemesi uygulanacak</AlertDialogTitle>
            <AlertDialogDescription>
              {preview?.to_faal_count} firmanın durumu FAAL olarak güncellenecek. Hâlihazırda FAAL olanlara ve komite
              bilgilerine dokunulmayacak. Bu işlem denetim kaydına yazılır.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="fs-confirm-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={apply} data-testid="fs-confirm-apply">Uygula</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
